import { ComponentType, ReactNode, useMemo } from 'react';

import {
  Area,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipElementProps,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';

import { useAnalyzeWithOpsPilot } from '@cast/features-organization-shared-opspilot';
import { useReactRouterStrategy } from 'components/chart/hooks';
import { HiddenDot } from 'components/chart/line/HiddenDot';
import { TimeSeries, TimeSeriesEntryWithTicks } from 'types/metrics';
import { attachTimeTicks } from 'utils/chart';
import { isE2E } from '@cast/utils';

import { QUERY_PARAM_KEYS } from '../../../../../cost-report/hooks/useCostReportTimeRangeSelection';
import { DataTransferChartDatapoint } from '../../../../../cost-report/types/networkCostOverTime';

type ChartDataItem = TimeSeriesEntryWithTicks<DataTransferChartDatapoint>;

type Props = {
  data: TimeSeries<DataTransferChartDatapoint>;
  isLoading?: boolean;
  unit: string;
  tooltipHeader?: ComponentType<ChartTooltipElementProps<ChartDataItem>>;
  tooltipFooter?: ComponentType<ChartTooltipElementProps<ChartDataItem>>;
  tooltipFormatValue?: (value: string | number | undefined) => ReactNode;
  yAxisProps?: Record<string, unknown>;
  timezone: string;
  buildPrompt?: (range?: { from: number; to: number }) => string | null;
};

const useNetworkCostZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: QUERY_PARAM_KEYS,
});

export const NetworkChart = ({
  data,
  isLoading,
  unit,
  yAxisProps,
  tooltipHeader,
  tooltipFooter,
  tooltipFormatValue,
  timezone,
  buildPrompt,
}: Props) => {
  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({ buildPrompt });
  const chartData = useMemo(() => attachTimeTicks(data), [data]);

  return (
    <ChartContainer<ChartDataItem>
      data={chartData}
      isLoading={isLoading}
      hasNoData={!data.length && !isLoading}
      sx={{ width: '100%', height: '100%', px: 16 }}
      zoomProps={{
        xAxisDataKey: 'chartTimestampUnix',
        useZoomState: useNetworkCostZoomState,
      }}
      analyzeWithOpsPilot={analyzeWithOpsPilot}
    >
      <Area<ChartDataItem>
        dataKey="egress"
        stroke="indigo.200"
        fill="indigo.200"
        tooltipConfig={{ displayName: 'Egress', unit }}
        dot={isE2E() ? HiddenDot : false}
      />
      <Area<ChartDataItem>
        dataKey="ingress"
        stroke="grey.700"
        fill="grey.700"
        tooltipConfig={{ displayName: 'Ingress', unit }}
        dot={isE2E() ? HiddenDot : false}
      />
      <ChartGrid hideVertical />

      <YAxis<ChartDataItem>
        {...yAxisProps}
        tickCount={3}
        padding={{ top: 30 }}
        type="number"
      />
      <TimeXAxis<ChartDataItem>
        unixDataKey="chartTimestampUnix"
        timezone={timezone}
        tick={{ fontSize: 10 }}
      />
      <ChartTooltip<ChartDataItem>
        testId="data-popper"
        header={tooltipHeader}
        footer={tooltipFooter}
        formatValue={(value) =>
          tooltipFormatValue ? tooltipFormatValue(value) : value
        }
      />
    </ChartContainer>
  );
};
